/**
 * Component-specific color logic
 * Each function takes (mode, palette) and returns only what that component needs.
 */

const getTextColors = (isDark, grey) => ({
  label:isDark ? grey[0] : grey[600],
  primary: isDark ? grey[0] : grey[600],
  secondary: isDark ? grey[0] : grey.A200,
  disabled: isDark ? grey[0] : grey[700],
  body: isDark ? grey[0] : grey.A900,
});

const getButtonColors = (isDark, grey, primary) => ({
  disabled: isDark ? grey[600] : grey[300],
  primary: primary.darker,
  border: isDark ? grey[0] : primary.darker,
  secondary: primary.dark,
  highlight:primary.highlight
});

const getNavColors = (isDark, grey,palette) => ({
  active: isDark ?  grey[0]: palette.primary.darker,
  active_text:isDark ?  grey[900]: grey[0],
  hover : isDark ?  grey[0]: palette.primary.lighter,
  inactive: isDark ? grey[0] : grey[0],
  in_active_text : isDark ? grey[500] : grey[600],
  disabled: isDark ? grey[600] : grey[400],
  sub_menu_in_active : isDark ? grey[600] : grey[0],
  sub_menu_in_active_text : isDark ? grey[600] : grey.A200
});

const getSurfaceColors = (isDark, grey) => ({
  divider: isDark ? grey[800] : grey[300],
  background: {
    paper: isDark ? grey[900] : grey[0],
    default: isDark ? '#121212' : grey.A50,
    layout:isDark ?  grey[900] : grey[200],
    boxColor:isDark ? grey[900] : grey[300],
  },
});

const getTableColor = (isDark, grey,palette) => ({
  head:{
    bgcolor: isDark ? grey[900] : grey[50],
    txtcolor: isDark ? grey[0] : grey[600],
  },
  hover: isDark ? grey[900] : grey[200],
  selected: isDark ? grey[900] : palette.primary.selected,
});
const getStatusColors = () => {
  //shared style
  const completedStyle = {
    bg: "#80d1ab",
    color: "#214534",
    label: "Completed"
  }

  return {
    generating: {
      bg: "#6fc6e8",
      color: "#2c6c85",
      label: "Processing"
    },
    failed: {
      bg: "#fa8c91",
      color: "#c9141c",
      label: "Failed"
    },
    generated: completedStyle,
    noData: completedStyle,
    queued: {
      bg: "#cbd5e1",
      color: "#475569",
      label: "Queued"
    }
  };
};

const getWorkflowColors = () => ({
  todo: {
    main: '#707278',
    bg: '#F2F2F2',
    border: '#85878C',
    text: '#707278'
  },
  inProgress: {
    main: '#B8732A',
    bg: '#FFF8F1',
    border: '#f5ddc5',
    text: '#B8732A'
  },
  completed: {
    main: '#26882A',
    bg: '#F2FFF3',
    border: '#A5D6A7',
    text: '#26882A'
  },
  blocked: {
    main: '#D32F2F',
    bg: '#FFF5F5',
    border: '#FFCDD2',
    text: '#D32F2F'
  }
});

import { extendPaletteWithChannels } from './utils/colorUtils';
import { buildPaletteColors, GREY_PALETTE } from './pattern-colors';
import ThemeOption from './theme';
import Typography from './typography';


export function buildPalette(mode) {
  const isDark = mode === 'dark';
  
  // 1. Initialize Base Colors
  const rawColors = buildPaletteColors();
  const paletteColor = ThemeOption({ ...rawColors, grey: GREY_PALETTE });
  
  // 2. Process for CSS variables (Channels)
  const palette = extendPaletteWithChannels(paletteColor);
  const common = extendPaletteWithChannels({ common: { black: '#000', white: '#fff' } });

  // 3. Compose Component-Wise
  return {
    mode,
    ...common,
    ...palette,
    Typography,
    
    // Component-wise assignments
    text: getTextColors(isDark, palette.grey),
    button: getButtonColors(isDark, palette.grey, palette.primary),
    navItems: getNavColors(isDark, palette.grey,palette),
    borderColor: isDark ? palette.grey[0] : palette.grey[400],
    table: getTableColor(isDark, palette.grey,palette),
    status: getStatusColors(),
    workflow: getWorkflowColors(),
    ...getSurfaceColors(isDark, palette.grey),
  };
}